/**
 * Dashboard.tsx — Command Center (the page that lives inside the platform shell).
 *
 * Phase 10 rewrite: replaces the legacy stack of Stats / Health / DriftTrends /
 * TopRisks / RecentActivity widgets with the editorial hybrid layout from
 * `04-hybrid-recommended.html`.
 *
 * Visual flow:
 *   1. Standfirst    — kicker pill, italic-mixed headline, deck, byline + ScoreBox
 *   2. PostureTabs   — sticky pill toggle (Posture / Operations / History)
 *   3. Tab content
 *      - Posture     → Triptych + PriorityQueue + ActivityStrip
 *      - Operations  → OperationsPanel (force-dark NOC console)
 *      - History     → coming-soon placeholder
 *
 * Existing legacy components in components/Dashboard/* are NOT touched.
 * They may still be imported elsewhere; Phase 12 cleanup will remove the
 * unused ones once the page migration settles.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { api } from '@/lib/api'
import {
  calculateStarredBpaScore,
  calculateUnifiedGuardianScore,
  useComplianceScores,
  useTriggerEvaluation,
} from '@/hooks/useComplianceData'
import { useTenantContext } from '@/hooks/useTenantContext'
import { usePostureBrief } from '@/hooks/usePostureBrief'
import { usePriorityActions } from '@/hooks/usePriorityActions'

import DashboardEmptyState from '@/components/Dashboard/DashboardEmptyState'
import QuickActions from '@/components/Dashboard/QuickActions'

import Standfirst from '@/components/Dashboard/v2/Standfirst'
import PostureTabs, { useDashboardTab } from '@/components/Dashboard/v2/PostureTabs'
import Triptych from '@/components/Dashboard/v2/Triptych'
import PriorityQueue from '@/components/Dashboard/v2/PriorityQueue'
import ActivityStrip from '@/components/Dashboard/v2/ActivityStrip'
import OperationsPanel from '@/components/Dashboard/v2/OperationsPanel'
import HistoryPanel from '@/components/Dashboard/v2/HistoryPanel'

export default function Dashboard() {
  const queryClient = useQueryClient()
  const { defaultTenantId, isLoading: contextLoading, userTenants, isGlobalAdmin } = useTenantContext()
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined)
  const [isRunningFullEval, setIsRunningFullEval] = useState(false)
  const triggerEvaluationMutation = useTriggerEvaluation()

  // Resolve selected tenant once context loads. Same logic as the legacy page —
  // global admins see an empty state until they pick, regular users default to
  // their own tenant.
  useEffect(() => {
    if (contextLoading) return
    if (isGlobalAdmin) {
      if (defaultTenantId) setSelectedTenantId(defaultTenantId)
      // else leave undefined — DashboardEmptyState will handle picker
    } else {
      if (defaultTenantId !== undefined) {
        setSelectedTenantId(defaultTenantId)
      } else if (userTenants.length > 0) {
        setSelectedTenantId(userTenants[0].tenantId)
      }
    }
  }, [contextLoading, defaultTenantId, isGlobalAdmin, userTenants])

  // Tenant changes propagate from the sidebar TenantContextBlock via
  // saveTenantSelection() + page reload — no per-page handler needed.
  // DashboardEmptyState ignores its onTenantSelect prop now (kept for API
  // back-compat); we pass a no-op.
  const handleTenantChange = () => {}

  // ---- Data queries (reuse the same keys as the legacy page so the cache
  //      remains warm if the user navigates back-and-forth) ----

  const {
    data: dashboardStats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['dashboard', selectedTenantId],
    queryFn: () => (selectedTenantId ? api.getDashboardStats(selectedTenantId) : Promise.resolve(null)),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: secureScoreSummary, isLoading: secureScoreLoading } = useQuery({
    queryKey: ['secure-score-summary', selectedTenantId],
    queryFn: () => api.getSecureScoreSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: userInsights, isLoading: userInsightsLoading } = useQuery({
    queryKey: ['user-insights', selectedTenantId],
    queryFn: () => api.getUserInsights(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: complianceScores, isLoading: complianceScoresLoading } =
    useComplianceScores(selectedTenantId)

  // Starred BPA score — only includes frameworks the user has opted into the
  // composite. Reads `weightedScore` (criticality-weighted) so the brief and
  // composite-bar tile match the BPA pillar's hero metric, which sources the
  // same field via `transformToDisplayFrameworks`. Using `adjustedScore` here
  // produced the "BPA tile shows 23% but brief names Identity (33%) as the
  // soft spot" divergence.
  const bpaStarredScore = useMemo(() => {
    if (!complianceScores?.standardScores) return undefined
    const mapped = complianceScores.standardScores.map(s => ({
      code: s.standardCode,
      score: s.weightedScore,
      weight: s.weight,
      includeInGuardianScore: s.includeInGuardianScore,
    }))
    return calculateStarredBpaScore(mapped) ?? undefined
  }, [complianceScores?.standardScores])

  const guardianScore = useMemo(
    () =>
      calculateUnifiedGuardianScore({
        secureScore: secureScoreSummary?.score?.percentage ?? null,
        bpaScore: bpaStarredScore ?? null,
        mfaAdoption: userInsights?.mfaMemberAdoptionPercentage ?? null,
        configCompliance: dashboardStats?.complianceScore?.percentage ?? null,
      }),
    [
      secureScoreSummary?.score?.percentage,
      bpaStarredScore,
      userInsights?.mfaMemberAdoptionPercentage,
      dashboardStats?.complianceScore?.percentage,
    ],
  )

  const compositeBars = useMemo(
    () => [
      {
        label: 'Baseline drift',
        weight: '20%',
        value: Math.round(dashboardStats?.complianceScore?.percentage ?? 0),
        fillClass: 'bg-primary-500',
      },
      {
        label: 'Best practice',
        weight: '30%',
        value: Math.round(bpaStarredScore ?? 0),
        fillClass: 'bg-secondaryBlue-300',
      },
      {
        label: 'MFA adoption',
        weight: '20%',
        value: Math.round(userInsights?.mfaMemberAdoptionPercentage ?? 0),
        fillClass: 'bg-secondarySand-300',
      },
      {
        label: 'M365 Secure Score',
        weight: '30%',
        value: Math.round(secureScoreSummary?.score?.percentage ?? 0),
        fillClass: 'bg-primary-500',
      },
    ],
    [
      dashboardStats?.complianceScore?.percentage,
      bpaStarredScore,
      userInsights?.mfaMemberAdoptionPercentage,
      secureScoreSummary?.score?.percentage,
    ],
  )

  // Top priority action — feeds the Standfirst brief so the headline verdict
  // names the same fix the PriorityQueue ranks #1. Without this, the brief
  // falls through to its weakestPillar branch and can recommend a different
  // pillar than the queue's top row, leaving the user with two competing
  // "what to fix first" stories on the same page. We pull a single row at
  // pillar='all' so the brief speaks to whichever pillar currently leads.
  // The fetch keys on (tenantId, maxCount, pillar) and so doesn't cache-
  // share with PriorityQueue's maxCount=6 fetch — but it's a small payload
  // and React Query dedupes within the 5-minute staleTime regardless.
  const { actions: topActions } = usePriorityActions({
    tenantId: selectedTenantId,
    maxCount: 1,
    pillar: 'all',
  })
  const topAction = topActions[0]
  const topActionForBrief = useMemo(() => {
    if (!topAction) return undefined
    // posture-brief.ts only models {crit, high, med} for impact emphasis —
    // narrow low/routine down to 'med' rather than dropping the action,
    // since the title is the load-bearing signal in the deck regardless.
    const impact: 'crit' | 'high' | 'med' =
      topAction.severity === 'crit' ? 'crit'
      : topAction.severity === 'high' ? 'high'
      : 'med'
    return {
      pillar: topAction.pillar,
      title: topAction.title,
      impact,
    }
  }, [topAction])

  const brief = usePostureBrief({
    tenantId: selectedTenantId,
    score: guardianScore,
    pillarScores: {
      compliance: dashboardStats?.complianceScore?.percentage ?? undefined,
      bpa: bpaStarredScore ?? undefined,
      mfa: userInsights?.mfaMemberAdoptionPercentage ?? undefined,
      secureScore: secureScoreSummary?.score?.percentage ?? undefined,
    },
    topAction: topActionForBrief,
  })

  const briefLoading =
    statsLoading || secureScoreLoading || userInsightsLoading || complianceScoresLoading

  const [tab] = useDashboardTab()

  // ---- Quick action: run full evaluation ----
  // Fires all five workloads in parallel — drift baseline, BPA/standards eval,
  // Secure Score refresh, User Insights refresh, and OAuth App Posture sync —
  // to satisfy the ActivityStrip QuickItem promise: "Baselines, best-practice,
  // identity, and apps in one pass." `triggerComplianceEvaluation` is the long
  // pole (synchronous, up to 5min); `syncOAuthApps` returns immediately and
  // continues server-side (AppPostureTab polls `syncInProgress` separately).
  // Promise.allSettled lets a single step fail without dropping the others.
  const handleRunFullEvaluation = async () => {
    if (!selectedTenantId || isRunningFullEval) return
    setIsRunningFullEval(true)

    const loadingId = toast.loading(
      'Running full evaluation: drift, best-practice, identity, and apps…',
    )

    const results = await Promise.allSettled([
      api.scanTenant(selectedTenantId),
      triggerEvaluationMutation.mutateAsync({
        tenantId: selectedTenantId,
        options: { forceRefresh: true },
      }),
      api.refreshSecureScore(selectedTenantId),
      api.refreshUserInsights(selectedTenantId),
      api.syncOAuthApps(selectedTenantId),
    ])

    const labels = [
      'Baseline drift',
      'Best-practice',
      'Secure Score',
      'User analytics',
      'App Posture',
    ]
    const failed = results
      .map((r, i) => (r.status === 'rejected' ? labels[i] : null))
      .filter((x): x is string => x !== null)

    toast.dismiss(loadingId)
    if (failed.length === 0) {
      toast.success(
        'Full evaluation complete. Drift, best-practice, identity, and apps refreshed.',
      )
    } else if (failed.length === results.length) {
      toast.error('Full evaluation failed. Please try again.')
    } else {
      toast.error(`Partial evaluation. ${failed.join(', ')} failed; other steps succeeded.`)
    }

    // Refresh dashboard surfaces. The compliance mutation already invalidates
    // its own keys (scores, violations, summary, evaluations); cover the rest.
    // App Posture summary continues syncing server-side; invalidating the
    // summary key lets AppPostureTab pick up `syncInProgress=true` and start
    // its 10s poll loop without the user navigating away and back.
    queryClient.invalidateQueries({ queryKey: ['dashboard', selectedTenantId] })
    queryClient.invalidateQueries({ queryKey: ['secure-score-summary', selectedTenantId] })
    queryClient.invalidateQueries({ queryKey: ['user-insights', selectedTenantId] })
    queryClient.invalidateQueries({ queryKey: ['oauth-apps-summary', selectedTenantId] })
    queryClient.invalidateQueries({ queryKey: ['oauth-apps', selectedTenantId] })

    setIsRunningFullEval(false)
  }

  // ---- Early returns: error / loading / empty tenant state ----

  if (statsError) {
    return (
      <div className="mx-auto max-w-3xl py-12 text-center">
        <h1 className="font-display text-2xl font-bold text-slate-100 dark:text-slate-100">
          We couldn&rsquo;t load the Command Center.
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          The dashboard endpoint returned an error. Try again, or refresh in a moment.
        </p>
        <button onClick={() => refetchStats()} className="btn-primary mt-6">
          Retry
        </button>
      </div>
    )
  }

  if (contextLoading) {
    return (
      <div className="mx-auto max-w-3xl py-16 text-center text-sm text-slate-500">
        Loading tenant context…
      </div>
    )
  }

  if (!selectedTenantId && isGlobalAdmin && userTenants.length > 0) {
    return (
      <DashboardEmptyState
        tenants={userTenants}
        selectedTenantId={selectedTenantId}
        onTenantSelect={handleTenantChange}
      />
    )
  }

  if (!selectedTenantId && userTenants.length === 0) {
    return (
      <div className="mx-auto max-w-3xl py-16 text-center text-sm text-slate-500">
        No tenants available. Please contact your administrator.
      </div>
    )
  }

  // ---- Main render ----

  return (
    <div className="mx-auto max-w-[1320px]">
      <Standfirst
        brief={brief}
        score={guardianScore}
        components={compositeBars}
        isLoading={briefLoading}
      />

      <PostureTabs
        actions={
          <QuickActions
            selectedTenantId={selectedTenantId}
            pendingProcessingCount={dashboardStats?.pendingProcessing ?? 0}
            onScanStarted={() => refetchStats()}
            onRefresh={() => refetchStats()}
            isRefreshing={statsLoading}
          />
        }
      />

      {tab === 'posture' && (
        <div
          id="panel-posture"
          role="tabpanel"
          aria-labelledby="tab-posture"
        >
          <Triptych
            tenantId={selectedTenantId}
            stats={dashboardStats}
            statsLoading={statsLoading}
            insights={userInsights}
            insightsLoading={userInsightsLoading}
          />

          <PriorityQueue tenantId={selectedTenantId} />

          <ActivityStrip
            stats={dashboardStats}
            isLoading={statsLoading}
            onRunEvaluation={handleRunFullEvaluation}
            isRunningEvaluation={isRunningFullEval}
          />
        </div>
      )}

      {tab === 'operations' && (
        <OperationsPanel
          tenantId={selectedTenantId}
          guardianScore={guardianScore}
          stats={dashboardStats}
          insights={userInsights}
          bpaScore={bpaStarredScore}
        />
      )}

      {tab === 'history' && <HistoryPanel />}
    </div>
  )
}
